import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import type { DraftableRow } from './useEditSession';
import { isUnvalidated } from './utils';

export interface DraftAutosaverProps<Row extends DraftableRow> {
  fieldArrayName: string;
  updateRow: (row: Row) => void;
}

export const DraftAutosaver = <Row extends DraftableRow>({
  fieldArrayName,
  updateRow,
}: DraftAutosaverProps<Row>) => {
  const form = useFormContext();

  useEffect(() => {
    const prefix = `${fieldArrayName}.`;

    const subscription = form.watch((_values, { name }) => {
      if (!name?.startsWith(prefix)) {
        return;
      }

      const [rowIndexPart, fieldName] = name.slice(prefix.length).split('.');
      const rowIndex = Number(rowIndexPart);

      if (Number.isNaN(rowIndex)) {
        throw new Error(
          `Unexpected field name format: ${name}. Expected format: ${fieldArrayName}.[index].fieldName`,
        );
      }

      if (fieldName === undefined || fieldName === 'id') {
        return;
      }

      const row = form.getValues(`${fieldArrayName}.${rowIndex}`) as Row;

      // If a row does not have a real numerical ID, then it has not yet been inserted
      // to a DB table (real or shadow), so it still must be in unvalidated insert limbo.
      if (isUnvalidated(row)) {
        return;
      }

      updateRow(row);
    });

    return () => subscription.unsubscribe();
  }, [form, fieldArrayName, updateRow]);

  return null;
};
